var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { css as cssLang } from '@codemirror/lang-css';
import { html as htmlLang } from '@codemirror/lang-html';
import { javascript } from '@codemirror/lang-javascript';
import CodeMirror from '@uiw/react-codemirror';
import { useMemo, useRef, useState } from 'react';
import { Expand } from 'iconoir-react';
import { classNames as cn } from '../utils';
import Button from './Button';
import CodeInputModal from './CodeInputModal';
import FieldTitle from './FieldTitle';
export default function CodeInput(_a) {
    var { className, title, validation, language = 'javascript', value = '', onChange, inlineLines = 5, disabled, readOnly, placeholder, titleClassName, id } = _a, _props = __rest(_a, ["className", "title", "validation", "language", "value", "onChange", "inlineLines", "disabled", "readOnly", "placeholder", "titleClassName", "id"]);
    const [open, setOpen] = useState(false);
    const localValRef = useRef(value);
    // Choose CodeMirror language extension
    const cmExtensions = useMemo(() => {
        switch (language) {
            case 'css':
                return [cssLang()];
            case 'html':
                return [htmlLang()];
            case 'typescript':
                return [javascript({ typescript: true })];
            case 'tsx':
                return [javascript({ typescript: true, jsx: true })];
            case 'javascript':
            default:
                return [javascript()];
        }
    }, [language]);
    // Format code using prettier (optional)
    // const format = async () => {
    //   try {
    //     // Lazy import avoids bundling if you don't use it
    //     const prettier = await import('prettier/standalone');
    //     const parserBabel = await import('prettier/parser-babel');
    //     const parser = 'babel-ts'; // handles ts/js/jsx nicely
    //     const formatted = await prettier.format(localValRef.current, {
    //       parser,
    //       plugins: [parserBabel],
    //       semi: true,
    //       singleQuote: false,
    //     });
    //     onChange?.(formatted);
    //   } catch {
    //     // silently ignore if not installed
    //   }
    // };
    return (_jsxs("div", { className: "relative flex flex-col justify-start gap-1", children: [title && _jsx(FieldTitle, { title: title, className: titleClassName }), _jsxs("div", { className: "relative", children: [_jsx("div", { className: cn('border-base-200 flex w-full overflow-hidden rounded-lg border bg-white transition-all', 'hover:border-base-300 focus-within:border-base-900! focus-within:ring-base-200 focus-within:ring-2', (validation === null || validation === void 0 ? void 0 : validation.status) === 'error' ? 'border-red-300 ring-red-300' : '', (validation === null || validation === void 0 ? void 0 : validation.status) === 'success'
                            ? 'border-green-300 ring-green-300'
                            : '', className), "aria-invalid": (validation === null || validation === void 0 ? void 0 : validation.status) === 'error' ? 'true' : undefined, "aria-describedby": (validation === null || validation === void 0 ? void 0 : validation.text) ? 'validation-text' : undefined, children: _jsx(CodeMirror, { value: value, onChange: (v) => {
                                localValRef.current = v;
                                onChange === null || onChange === void 0 ? void 0 : onChange(v);
                            }, extensions: cmExtensions, basicSetup: {
                                lineNumbers: true,
                                highlightActiveLine: false,
                                autocompletion: true,
                                foldGutter: true,
                            }, height: `${inlineLines * 22 + 16}px`, minWidth: "100%", width: "100%", editable: !disabled, placeholder: placeholder, style: {
                                fontSize: 13,
                                width: '100%',
                            }, readOnly: readOnly }) }), _jsxs("div", { className: "absolute top-2 right-2 flex items-center gap-2", children: [_jsx(Button, { variant: "outline", size: 'icon', onClick: () => setOpen(true), children: _jsx(Expand, { className: "h-4 w-4 text-gray-600" }) }), _jsx(CodeInputModal, { open: open, onClose: () => setOpen(false), title: title || 'Code Editor', language: language, value: value, onChange: onChange, disabled: disabled, placeholder: placeholder })] })] }), (validation === null || validation === void 0 ? void 0 : validation.text) && (_jsx("p", { id: "validation-text", className: cn('mt-1 text-sm', validation.status === 'error' ? 'text-red-600' : '', validation.status === 'success' ? 'text-green-600' : ''), children: validation.text }))] }));
}
